import type { System } from '@/core/system';
import type { Engine } from '@/core/engine';
import type { Time } from '@/core/time';
import type { City } from '@/world/city';
import type { Ground } from '@/rendering/ground';
import type { BuildingField } from '@/rendering/building-field';
import type { SkyLinkField } from '@/rendering/skylink-field';
import type { OrnamentField } from '@/rendering/ornament-field';
import type { PlayerController } from '@/player/player-controller';
import type { PathRecorder } from '@/systems/path-recorder';
import type { NarrativeController } from '@/ai/narrative-controller';
import type { FinalePhase } from '@/core/types';
import { FINALE } from '@/config';
import { smoothstep, lerp } from '@/utils/math';

/**
 * "The Remembering" — the finale ceremony director.
 *
 * The thesis of Palimpsest ("the city is your reflection") was, at the end, only
 * asserted in a line of text. This system demonstrates it instead: a wordless-first,
 * ~60-second choreography that lifts the player above the city, inks the whole route
 * they ever walked across the floor, withdraws every structure their presence caused
 * back to the pale seed city, then reblooms it — before setting them down to keep
 * walking. It is a small state machine driven purely by accumulated frame-delta (no
 * wall clock), so it is deterministic and survives frame stalls gracefully.
 *
 * Interpolation of memory and reveal is eased with smoothstep on each phase's local
 * 0..1 progress. Evolution is paused for the duration and resumed at the descent.
 *
 * Constructor takes its collaborators explicitly (the composition root wires it):
 *   (engine, city, ground, buildings, links, ornaments, player, recorder)
 */

/** Internal state: 'idle' before the beat, 'hold' is the quiet part of recall. */
type DirectorState = 'idle' | 'hold' | FinalePhase;

/** The monolith (and thus the ceremony's spatial focus) grows at the city centre. */
const MONOLITH_X = 0;
const MONOLITH_Z = 0;

export class FinaleDirector implements System {
  readonly name = 'finale';

  private state: DirectorState = 'idle';
  /** Elapsed time within the current phase (seconds). */
  private phaseT = 0;
  /** True once the ceremony has begun — guards the "at most once per session" rule. */
  private started = false;

  // summon
  private rippleTimer = 0;

  // recall / rebloom — the wave is centred on the player's XZ at the apex.
  private centreX = 0;
  private centreZ = 0;

  // ascend
  private pathPainted = false;

  constructor(
    private engine: Engine,
    private city: City,
    private ground: Ground,
    private buildings: BuildingField,
    private links: SkyLinkField,
    private ornaments: OrnamentField,
    private player: PlayerController,
    private recorder: PathRecorder,
  ) {}

  init(): void {
    // If the save shows the ceremony was already witnessed, restore the epilogue
    // state silently — no lift, no beats, just the path resting at its faint
    // permanent strength. Detect via the narrative controller's fired set.
    const narrative = this.engine.get<NarrativeController>('narrative');
    if (narrative?.hasFired('finale')) {
      this.restoreEpilogue();
      return;
    }
    // Otherwise wait for the (wordless) finale beat to summon the ceremony.
    this.engine.events.on('narrative:beat', (b) => {
      if (b.id === 'finale') this.beginSummon();
    });
  }

  private restoreEpilogue(): void {
    this.started = true;
    this.state = 'done';
    this.ground.paintPath(this.recorder.points());
    this.ground.setPathReveal(FINALE.pathEpilogueStrength);
  }

  update(time: Time): void {
    const dt = time.delta;
    switch (this.state) {
      case 'summon':
        this.updateSummon(dt);
        break;
      case 'ascend':
        this.updateAscend(dt);
        break;
      case 'recall':
        this.updateRecall(dt);
        break;
      case 'hold':
        this.updateHold(dt);
        break;
      case 'rebloom':
        this.updateRebloom(dt);
        break;
      case 'descend':
        this.updateDescend(dt);
        break;
      // 'idle' and 'done' are inert.
    }
  }

  /* ---------------- summon ---------------- */

  private beginSummon(): void {
    if (this.started) return; // once per session
    this.started = true;
    this.state = 'summon';
    this.phaseT = 0;
    this.rippleTimer = 0;
    // A glowing light-disc waits at the monolith base. Offset slightly off-centre so
    // its flat disc doesn't z-fight the monolith's own footprint.
    this.city.addOrnament({
      kind: 'pool',
      x: 2.5,
      y: 0.05,
      z: 0,
      yaw: 0,
      scale: 2.2,
      seed: 7,
    });
    this.emitPhase('summon');
  }

  private updateSummon(dt: number): void {
    this.phaseT += dt;
    this.rippleTimer += dt;
    if (this.rippleTimer >= 6) {
      this.rippleTimer -= 6;
      this.ground.ripple(MONOLITH_X, MONOLITH_Z, 1.6);
    }
    // Trigger when the player comes near the monolith, or after the grace period
    // from wherever they stand.
    const p = this.player.position;
    const dist = Math.hypot(p.x - MONOLITH_X, p.z - MONOLITH_Z);
    if (dist <= FINALE.approachRadius || this.phaseT >= FINALE.graceSeconds) {
      this.beginAscend();
    }
  }

  /* ---------------- ascend ---------------- */

  private beginAscend(): void {
    this.setEvolutionPaused(true);
    this.state = 'ascend';
    this.phaseT = 0;
    this.pathPainted = false;
    this.player.liftTo(FINALE.riseHeight, FINALE.riseSeconds);
    this.emitPhase('ascend');
  }

  private updateAscend(dt: number): void {
    this.phaseT += dt;
    // Mid-rise, the ink line of everywhere they walked draws itself in.
    if (!this.pathPainted && this.phaseT >= FINALE.pathRevealAt) {
      this.pathPainted = true;
      this.ground.paintPath(this.recorder.points());
    }
    if (this.pathPainted) {
      const r = smoothstep((this.phaseT - FINALE.pathRevealAt) / FINALE.pathRevealSeconds);
      this.ground.setPathReveal(r);
    }
    if (this.phaseT >= FINALE.riseSeconds) {
      this.ground.setPathReveal(1);
      this.beginRecall();
    }
  }

  /* ---------------- recall ---------------- */

  private beginRecall(): void {
    // The wave centres on the player's XZ at the apex.
    this.centreX = this.player.position.x;
    this.centreZ = this.player.position.z;
    this.state = 'recall';
    this.phaseT = 0;
    this.emitPhase('recall');
    this.emitBeat('finale-recall', FINALE.texts.recall);
  }

  private updateRecall(dt: number): void {
    this.phaseT += dt;
    const m = smoothstep(this.phaseT / FINALE.recallSeconds);
    this.applyMemory(m);
    if (this.phaseT >= FINALE.recallSeconds) {
      this.applyMemory(1);
      this.beginHold();
    }
  }

  private beginHold(): void {
    // The seed city holds, pale and quiet. This is part of the recall phase, so no
    // new finale:phase event fires — memory simply stays at 1.
    this.state = 'hold';
    this.phaseT = 0;
  }

  private updateHold(dt: number): void {
    this.phaseT += dt;
    if (this.phaseT >= FINALE.holdSeconds) this.beginRebloom();
  }

  /* ---------------- rebloom ---------------- */

  private beginRebloom(): void {
    this.state = 'rebloom';
    this.phaseT = 0;
    this.emitPhase('rebloom');
    this.emitBeat('finale-rebloom', FINALE.texts.rebloom);
  }

  private updateRebloom(dt: number): void {
    this.phaseT += dt;
    const m = 1 - smoothstep(this.phaseT / FINALE.rebloomSeconds);
    this.applyMemory(m);
    if (this.phaseT >= FINALE.rebloomSeconds) {
      this.applyMemory(0);
      this.beginDescend();
    }
  }

  /* ---------------- descend ---------------- */

  private beginDescend(): void {
    this.state = 'descend';
    this.phaseT = 0;
    this.player.liftTo(0, FINALE.descendSeconds);
    this.emitPhase('descend');
  }

  private updateDescend(dt: number): void {
    this.phaseT += dt;
    // Settle the path ink from full down to its faint permanent strength over ~4s.
    const t = smoothstep(this.phaseT / 4);
    this.ground.setPathReveal(lerp(1, FINALE.pathEpilogueStrength, t));
    if (this.phaseT >= FINALE.descendSeconds) {
      this.player.releaseLift();
      this.setEvolutionPaused(false);
      this.ground.setPathReveal(FINALE.pathEpilogueStrength);
      this.beginDone();
    }
  }

  /* ---------------- done ---------------- */

  private beginDone(): void {
    this.state = 'done';
    this.applyMemory(0); // ensure the world is fully rebloomed
    this.emitPhase('done');
    this.emitBeat('finale-done', FINALE.texts.done);
  }

  /* ---------------- helpers ---------------- */

  private applyMemory(m: number): void {
    this.buildings.setMemory(m, this.centreX, this.centreZ);
    this.links.setMemory(m);
    this.ornaments.setMemory(m);
  }

  private setEvolutionPaused(paused: boolean): void {
    const evo = this.engine.get<System & { paused: boolean }>('evolution');
    if (evo) evo.paused = paused;
  }

  private emitPhase(phase: FinalePhase): void {
    this.engine.events.emit('finale:phase', { phase });
  }

  private emitBeat(id: string, text: string): void {
    this.engine.events.emit('narrative:beat', { id, text, progress: 1 });
  }
}
